import { useQuery } from '@tanstack/react-query';
import { DailyChart } from '../components/DailyChart';
import { AppLink } from '../components/Nav';
import { Stat } from '../components/data';
import { ErrorState, Loading, PageHeader } from '../components/ui';
import { errorMessage } from '../lib/api';
import { bytes, durationLabel } from '../lib/format';
import { fetchStats, qk } from '../queries/admin';

/** The one page to look at every morning. Numbers first, then the last month as two charts. */
export function OverviewPage() {
  const stats = useQuery({ queryKey: qk.stats(), queryFn: fetchStats });

  if (stats.isPending) {
    return (
      <>
        <PageHeader title="Overview" />
        <Loading />
      </>
    );
  }
  if (stats.isError) {
    return (
      <>
        <PageHeader title="Overview" />
        <ErrorState message={errorMessage(stats.error)} onRetry={() => void stats.refetch()} />
      </>
    );
  }

  const data = stats.data;
  const paying = (data.orgs.byStatus.active ?? 0) + (data.orgs.byStatus.past_due ?? 0);
  const failed = data.videos.byStatus.failed ?? 0;
  const processing = (data.videos.byStatus.processing ?? 0) + (data.videos.byStatus.uploading ?? 0);

  return (
    <>
      <PageHeader title="Overview" subtitle="What Slate looks like right now." />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <AppLink to="/users">
          <Stat label="Users" value={data.users.total.toLocaleString()} hint={`${data.users.last7d.toLocaleString()} this week, ${data.users.last30d.toLocaleString()} this month`} />
        </AppLink>
        <AppLink to="/teams">
          <Stat
            label="Paying teams"
            value={paying.toLocaleString()}
            hint={`${(data.orgs.byStatus.trialing ?? 0).toLocaleString()} on trial, ${(data.orgs.byStatus.past_due ?? 0).toLocaleString()} past due, ${data.orgs.total.toLocaleString()} teams in all`}
          />
        </AppLink>
        <AppLink to="/recordings">
          <Stat
            label="Recordings"
            value={data.videos.total.toLocaleString()}
            hint={`${data.videos.last7d.toLocaleString()} this week, ${durationLabel(data.videos.totalDurationSec)} in all${data.videos.totalBytes ? `, ${bytes(data.videos.totalBytes)}` : ''}`}
          />
        </AppLink>
        <AppLink to="/recordings?status=failed">
          <Stat
            label="Failed"
            value={failed.toLocaleString()}
            tone={failed > 0 ? 'danger' : undefined}
            hint={processing > 0 ? `${processing.toLocaleString()} still processing` : 'nothing processing'}
          />
        </AppLink>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <AppLink to="/requests">
          <Stat label="Open requests" value={data.featureRequests.open.toLocaleString()} tone={data.featureRequests.open > 0 ? 'caution' : undefined} hint="feature requests not yet handled" />
        </AppLink>
        <AppLink to="/admins">
          <Stat label="Admins" value={data.admins.total.toLocaleString()} hint="people who can see this" />
        </AppLink>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <DailyChart title="Signups per day" points={data.daily.signups} color="#7FB2D9" unit={{ singular: 'signup', plural: 'signups' }} />
        <DailyChart title="Recordings per day" points={data.daily.recordings} color="#5FAF88" unit={{ singular: 'recording', plural: 'recordings' }} />
      </div>
    </>
  );
}
